"use client";

import { Icon, MainButton } from "@repo/dashboard-core";
import { useTranslations } from "next-intl";
import React from "react";

import type { EsimProduct } from "@/lib/types/esim";
import { planDataAllowance, validityUnitKey } from "@/lib/utils/esim";

function PlanCardInner({
  product,
  actionLabel,
  onSelect,
}: {
  product: EsimProduct;
  actionLabel: string;
  onSelect: (product: EsimProduct) => void;
}) {
  const t = useTranslations("esim");
  const allowance = planDataAllowance(product);
  const coverage = product.country?.coverage ?? "country";
  const unitKey = product.validity
    ? validityUnitKey(product.validity.unit)
    : null;

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl bg-surface shadow-shell-sm transition-shadow hover:shadow-shell-md">
      <div className="flex items-start justify-between gap-3 border-b border-surface-border p-4">
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-text-primary">
            {product.country?.name ?? product.name}
          </p>
          {/* Not a <p>: Icon renders a div, which would close the paragraph
              early and break hydration. */}
          <div className="mt-0.5 flex items-center gap-1.5 text-sm text-text-muted">
            <Icon name="globe" size={14} />
            {t(`coverage-${coverage}`)}
          </div>
        </div>
        {allowance && (
          <span className="shrink-0 rounded-lg bg-primary-light px-2.5 py-1 text-base font-bold text-primary">
            {allowance}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <p className="line-clamp-2 text-base text-text-secondary">
          {product.name}
        </p>

        <dl className="mt-auto space-y-1.5 pt-2 text-base">
          {product.validity && (
            <PlanFact
              label={t("plan-validity")}
              value={
                unitKey
                  ? t(`validity-${unitKey}`, {
                      count: product.validity.quantity,
                    })
                  : `${product.validity.quantity} ${product.validity.unit}`
              }
            />
          )}
          <PlanFact label={t("plan-price")} value={t("plan-included")} />
        </dl>
      </div>

      <div className="p-4 pt-0">
        <MainButton
          className="w-full"
          onClick={() => onSelect(product)}
          size="md"
        >
          {actionLabel}
        </MainButton>
      </div>
    </div>
  );
}

function PlanFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-text-muted">{label}</dt>
      <dd className="text-right font-medium text-text-primary">{value}</dd>
    </div>
  );
}

export const PlanCard = React.memo(PlanCardInner);
